import { useQuery } from "@tanstack/react-query";
import { createLogger } from "logging";
import { PatchBoardModel } from "models/PatchBoardModel";
import { useApi, usePermissions, useToasts, useTranslations } from "providers";
import { useBoardSettings } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { EditBoardConfigurationPanel } from "./components/EditBoardConfigurationPanel";
import {
  EditBoardConfigurationContext,
  EditBoardConfigurationContextProps,
  EditBoardConfigurationFieldValues,
  getEditBoardConfigurationFieldValues,
} from "./EditBoardConfigurationContext";

const logger = createLogger("EditBoardConfigurationProvider");

export const EditBoardConfigurationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { boardApi } = useApi();
  const i18n = useTranslations();
  const toasts = useToasts();
  const { boardId, boardSettings, reloadBoardSettings } = useBoardSettings();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmUnsaved, setConfirmUnsaved] = useState(false);

  const form = useForm<EditBoardConfigurationFieldValues>({
    defaultValues: undefined,
  });
  const { formState, getValues, reset } = form;
  const [editBoardId, setEditBoardId] = useState<number>(-1);

  const watchErrors = JSON.stringify(formState.errors, null, 2);
  const { isDirty, isValid } = formState;
  const isSubmittable = useMemo(() => isValid || watchErrors == "{}", [isValid, watchErrors]);

  const openEditBoardConfiguration = useCallback(() => {
    setEditBoardId(Number(boardId ?? -1));
    setIsOpen(true);
  }, [boardId]);

  const queryResult = useQuery({
    queryKey: [`Board|${editBoardId}|EditConfiguration`],
    queryFn: async ({ signal }) => {
      const data = await boardApi.getBoardSettings(editBoardId, signal);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: editBoardId > 0,
    refetchOnWindowFocus: false,
  });
  const { error } = queryResult;
  const isLoading = queryResult.isFetching;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  useEffect(() => {
    if (!queryResult.data) return;
    reset(getEditBoardConfigurationFieldValues(queryResult.data));
  }, [queryResult.data, reset]);

  const handleClose = useCallback(
    (ignoreDirty?: boolean) => {
      const confirm = isDirty && !ignoreDirty;
      logger.debug(`handleClose(): isDirty:=${isDirty} (ignore:=${ignoreDirty}) -> ${confirm}`);
      if (confirm) {
        setConfirmUnsaved(true);
      } else {
        reset();
        setIsOpen(false);
        setEditBoardId(-1);
        reloadBoardSettings();
      }
    },
    [isDirty, reset, reloadBoardSettings]
  );

  const getPatch = useCallback(
    (values: EditBoardConfigurationFieldValues) => {
      const patch: PatchBoardModel = {
        id: Number(boardId!),
        signatureFirstName: values.signatureFirstName,
        signatureLastName: values.signatureLastName,
        defaultDuration: values.defaultDuration,
        defaultTime: values.defaultTime,
        defaultLocation: values.defaultLocation,
        defaultRemarks: values.defaultRemarks,
      };
      return patch;
    },
    [boardId]
  );

  const handleSave = useCallback(async () => {
    logger.debug("handleSave():", isValid);
    if (!isValid) return;

    try {
      setIsSubmitting(true);
      const patch = getPatch(getValues());

      const entityTitle = boardSettings?.name ?? i18n.EntityTitle_Unknown;
      try {
        await boardApi.updateBoard(patch);
        toasts.updateSuccess(i18n.BoardSettings, entityTitle);
        handleClose(true);
      } catch (error) {
        toasts.updateFailure(i18n.BoardSettings, entityTitle, error);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [isValid, i18n, boardApi, toasts, boardSettings, getPatch, getValues, handleClose]);

  const contextValue: EditBoardConfigurationContextProps = useMemo(
    () => ({
      confirmUnsaved,
      setConfirmUnsaved,
      isDirty,
      isValid,
      isLoading,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
      openEditBoardConfiguration,
    }),

    [
      confirmUnsaved,
      isDirty,
      isValid,
      isLoading,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
      openEditBoardConfiguration,
    ]
  );

  return (
    <EditBoardConfigurationContext.Provider value={contextValue}>
      {children}
      {isOpen && <EditBoardConfigurationPanel />}
    </EditBoardConfigurationContext.Provider>
  );
};
